import { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { AlertCircle, ShieldOff, ChevronRight, ChevronLeft, Loader2, Check, MailMinus, Users } from 'lucide-react';
import { fetchEmail } from '@/api/emails';
import { useOperationStore } from '@/stores/operationStore';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { getInitials } from '@/features/email-list/utils/emailFormatting';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'motion/react';
import { slideInFromBottom } from '@/lib/animations';
import { SpamScoreBadge } from '@/components/SpamScoreBadge';
import { MessageBody } from '@/features/email-detail/components/MessageBody';
import { sanitizeEmailHtml } from '@/lib/emailHtmlSanitizer';
import type { SpamReport } from '@/types/email';

export interface PendingSenderCardData {
  key: string;
  accountId?: string | null;
  folderId?: string | null;
  emailId?: string | null;
  senderName: string | null;
  senderEmail: string;
  subject: string;
  snippet: string;
  date: Date | null;
  bodyHtml?: string | null;
  unsubscribeUrl?: string | null;
  operationState?: 'pending' | 'error';
  operationId?: string | null;
  hasEmail?: boolean;
  recipientEmail?: string | null;
  aiDecision?: 'yes' | 'no' | null;
  aiStatus?: 'pending' | 'running' | 'complete' | 'error' | null;
  aiReason?: string | null;
  spamScore?: number | null;
  spamRequiredScore?: number | null;
  spamAction?: string | null;
  spamReport?: SpamReport | null;
  spamScannedAt?: string | null;
}

interface NewSendersBannerProps {
  pendingSenders: PendingSenderCardData[];
  isLoading?: boolean;
  dialogOpen: boolean;
  onDialogOpenChange: (open: boolean) => void;
  onSelect: (key: string) => void;
  onApprove: (key: string) => void;
  onBlock: (key: string) => void;
  onUnsubscribe?: (key: string, unsubscribeUrl: string) => void;
  onApproveAll: () => Promise<void> | void;
  onBlockAll: () => Promise<void> | void;
  bulkActionState?: 'approve' | 'block' | null;
  bulkActionProgress?: { completed: number; total: number } | null;
  onOpenContacts?: () => void;
  className?: string;
}

interface PendingSenderCardProps {
  data: PendingSenderCardData;
  layout?: 'inline' | 'grid';
  isSelected?: boolean;
  onSelect: (key: string) => void;
  onApprove: (key: string) => void;
  onBlock: (key: string) => void;
  onUnsubscribe?: (key: string, unsubscribeUrl: string) => void;
  onAfterSelect?: () => void;
}

const PendingSenderCardInner = ({
  data,
  layout = 'inline',
  isSelected = false,
  onSelect,
  onApprove,
  onBlock,
  onUnsubscribe,
  onAfterSelect,
}: PendingSenderCardProps) => {
  const avatarSrc = getBrandfetchLogoUrl(data.senderEmail);
  // Read operation state directly from store to avoid re-rendering all cards
  // when any single card's operation state changes
  const storeOperationState = useOperationStore((state) =>
    data.operationId ? state.operationStates.get(data.operationId) ?? null : null
  );
  const effectiveOperationState = data.operationState ?? (
    storeOperationState === 'pending' ? 'pending' :
    storeOperationState === 'error' ? 'error' :
    undefined
  );
  const isProcessing = effectiveOperationState === 'pending';
  const hasError = effectiveOperationState === 'error';
  const subject = data.subject?.trim() || 'No subject';
  const snippet = data.snippet?.trim() || 'No preview available';
  const showAiDecision = data.aiStatus === 'complete' && Boolean(data.aiDecision);
  const aiReason = data.aiReason?.trim() || null;
  const aiTooltipText = aiReason || 'Reason unavailable';
  const aiLabel = data.aiDecision === 'yes' ? 'Review' : 'Skip';
  const aiBadgeClass = data.aiDecision === 'yes'
    ? 'bg-success/10 text-success-ink border-success/20'
    : 'bg-muted text-muted-foreground border-muted-foreground/20';
  const showSpamScore = data.spamScore !== null && data.spamScore !== undefined;
  const showDecisionRow = showAiDecision || Boolean(data.recipientEmail) || showSpamScore;

  const cardClass = cn(
    'group relative flex flex-col justify-between gap-2 rounded-2xl border p-4 text-left transition-all duration-300 overflow-hidden',
    'bg-card border-border shadow-xs hover:bg-accent/40',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
    layout === 'inline' && 'min-w-[340px] max-w-[340px]',
    layout === 'grid' && 'w-full min-w-0',
    hasError && 'border-destructive/30 bg-destructive/5',
    isSelected && 'ring-2 ring-primary border-primary/50 bg-primary/5'
  );

  return (
    <motion.div
      role="button"
      tabIndex={0}
      className={cardClass}
      whileHover={{ y: -2, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{
        type: "spring",
        stiffness: 350,
        damping: 25,
      }}
      onClick={() => {
        onSelect(data.key);
        onAfterSelect?.();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(data.key);
          onAfterSelect?.();
        }
      }}
    >
      <div className="flex items-start gap-3.5">
        <Avatar className="h-10 w-10 border border-border/40 shadow-sm mt-0.5">
          {avatarSrc && <AvatarImage src={avatarSrc} alt={data.senderName ?? data.senderEmail} />}
          <AvatarFallback className="bg-primary/5 text-primary/70 text-xs font-medium">
            {getInitials(data.senderName ?? undefined, data.senderEmail)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex flex-col min-w-0 flex-1 gap-1">
          <div className="flex items-center justify-between gap-2 min-w-0">
             <p className="truncate text-sm font-semibold text-foreground leading-tight min-w-0">
              {data.senderName ?? data.senderEmail}
            </p>
          </div>
          <p className="truncate text-xs text-muted-foreground font-medium">{data.senderEmail}</p>
          {showDecisionRow && (
            <div className="flex flex-wrap items-center gap-0.5">
              {showAiDecision && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className={cn('h-5 px-1.5 text-[10px] font-medium cursor-default border', aiBadgeClass)}
                    >
                      {aiLabel}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-60 text-xs">
                    {aiTooltipText}
                  </TooltipContent>
                </Tooltip>
              )}
              {data.recipientEmail && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="destructive"
                      className="h-5 px-1.5 text-[10px] bg-destructive/5 text-destructive/70 border-destructive/10 cursor-pointer hover:bg-destructive/10"
                    >
                      {data.recipientEmail.split('@')[0]}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-60 text-xs">
                    Sent to {data.recipientEmail}
                  </TooltipContent>
                </Tooltip>
              )}
              {showSpamScore && (
                <SpamScoreBadge
                  score={data.spamScore ?? undefined}
                  requiredScore={data.spamRequiredScore ?? undefined}
                  action={data.spamAction ?? undefined}
                  report={data.spamReport ?? undefined}
                  scannedAt={data.spamScannedAt ?? undefined}
                  className="h-5 shadow-none text-[10px] border-border bg-transparent px-1.5"
                />
              )}
            </div>
          )}
         
        </div>
      </div>

      <div className="flex flex-col gap-1 px-0.5 pt-0">
        <p className="text-sm font-medium text-foreground/90 truncate">{subject}</p>
        <p className="truncate sm:whitespace-normal sm:line-clamp-2 text-xs text-muted-foreground">{snippet}</p>
      </div>
      
      <div className="flex items-center gap-2 pt-0">
        <Button
          size="sm"
          variant="secondary"
          className={cn(
            "flex-1 h-8 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary border border-primary/10 shadow-none",
            isProcessing && "opacity-70 cursor-wait"
          )}
          disabled={isProcessing}
          onClick={(event) => {
            event.stopPropagation();
            onApprove(data.key);
          }}
        >
          {isProcessing ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          )}
          Approve
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className={cn(
            "flex-1 h-8 text-xs font-medium hover:text-destructive hover:bg-destructive/10",
            isProcessing && "opacity-70 cursor-wait"
          )}
          disabled={isProcessing}
          onClick={(event) => {
            event.stopPropagation();
            onBlock(data.key);
          }}
        >
          <ShieldOff className="mr-1.5 h-3.5 w-3.5" />
          Block
        </Button>
        {data.unsubscribeUrl && onUnsubscribe && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "h-8 w-8 p-0 text-muted-foreground hover:text-foreground",
                  isProcessing && "opacity-70 cursor-wait"
                )}
                disabled={isProcessing}
                onClick={(event) => {
                  event.stopPropagation();
                  onUnsubscribe(data.key, data.unsubscribeUrl!);
                }}
              >
                <MailMinus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Unsubscribe & archive
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      
      {hasError && (
        <p className="text-[10px] font-medium text-destructive text-center animate-pulse">
          Action failed. Please retry.
        </p>
      )}
    </motion.div>
  );
};

// Memoize to prevent re-renders when parent state changes (e.g., preview open/close)
const PendingSenderCard = memo(PendingSenderCardInner, (prevProps, nextProps) => {
  // Operation state is read from store inside the card, so we don't check it here.
  const a = prevProps.data;
  const b = nextProps.data;
  return (
    a.key === b.key &&
    a.operationId === b.operationId &&
    // Content hydrates AFTER mount (AI decision via realtime, spam scan,
    // unsubscribe URL, body/preview) — compare it or a mounted card freezes on
    // its initial empty state and never shows the hydrated values (X7).
    a.senderName === b.senderName &&
    a.subject === b.subject &&
    a.snippet === b.snippet &&
    a.bodyHtml === b.bodyHtml &&
    a.unsubscribeUrl === b.unsubscribeUrl &&
    a.hasEmail === b.hasEmail &&
    a.emailId === b.emailId &&
    (a.date?.getTime() ?? null) === (b.date?.getTime() ?? null) &&
    a.aiDecision === b.aiDecision &&
    a.aiStatus === b.aiStatus &&
    a.aiReason === b.aiReason &&
    a.spamScore === b.spamScore &&
    a.spamRequiredScore === b.spamRequiredScore &&
    a.spamAction === b.spamAction &&
    a.spamScannedAt === b.spamScannedAt &&
    a.spamReport === b.spamReport &&
    prevProps.layout === nextProps.layout &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.onApprove === nextProps.onApprove &&
    prevProps.onBlock === nextProps.onBlock &&
    prevProps.onUnsubscribe === nextProps.onUnsubscribe
  );
});

function formatPreviewDate(date: Date | null): string {
  if (!date) return '';
  const messageDate = new Date(date);
  return messageDate.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NewSendersBanner({
  pendingSenders,
  isLoading = false,
  dialogOpen,
  onDialogOpenChange,
  onSelect,
  onApprove,
  onBlock,
  onUnsubscribe,
  onApproveAll,
  onBlockAll,
  bulkActionState = null,
  bulkActionProgress = null,
  onOpenContacts,
  className,
}: NewSendersBannerProps) {
  const pendingCount = pendingSenders.length;
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollPositionRef = useRef<number>(0);

  // Cache for fetched message data (body and unsubscribe URL)
  const [fetchedEmailData, setFetchedEmailData] = useState<Map<string, { bodyHtml?: string; body?: string; unsubscribeUrl?: string }>>(new Map());
  const [isFetchingBody, setIsFetchingBody] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);

  // Find current preview index and data
  const previewIndex = useMemo(() => {
    if (!previewKey) return -1;
    return pendingSenders.findIndex((s) => s.key === previewKey);
  }, [previewKey, pendingSenders]);

  const previewData = previewIndex >= 0 ? pendingSenders[previewIndex] : null;

  // Fetch full message data when preview is opened and body is missing
  useEffect(() => {
    if (!previewData) return;

    // Already have body HTML from list or from cache
    if (previewData.bodyHtml || fetchedEmailData.has(previewData.key)) return;

    // Need accountId and emailId to fetch
    if (!previewData.accountId || !previewData.emailId) return;

    // Abort any existing fetch
    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    setIsFetchingBody(true);

    fetchEmail(previewData.accountId, previewData.emailId, previewData.folderId ?? undefined)
      .then((email) => {
        if (abortController.signal.aborted) return;
        // Cache body content and unsubscribe URL from fetched email
        setFetchedEmailData((prev) => new Map(prev).set(previewData.key, {
          bodyHtml: email.bodyHtml ?? undefined,
          body: email.body ?? undefined,
          unsubscribeUrl: email.unsubscribeUrl ?? undefined,
        }));
      })
      .catch((err) => {
        if (abortController.signal.aborted) return;
        console.error('[NewSendersBanner] Failed to fetch message body:', err);
        // Cache empty entry so we don't retry endlessly — snippet will show as fallback
        setFetchedEmailData((prev) => new Map(prev).set(previewData.key, {}));
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsFetchingBody(false);
        }
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchedEmailData is read for cache check only; key change is the real trigger
  }, [previewData?.key, previewData?.accountId, previewData?.emailId, previewData?.folderId, previewData?.bodyHtml]);

  // Navigation handlers
  const canGoPrev = previewIndex > 0;
  const canGoNext = previewIndex >= 0 && previewIndex < pendingSenders.length - 1;

  const handlePrev = useCallback(() => {
    if (canGoPrev) {
      setPreviewKey(pendingSenders[previewIndex - 1].key);
    }
  }, [canGoPrev, pendingSenders, previewIndex]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      setPreviewKey(pendingSenders[previewIndex + 1].key);
    }
  }, [canGoNext, pendingSenders, previewIndex]);

  const handleClosePreview = useCallback(() => {
    setPreviewKey(null);
    // Restore scroll position after React renders the grid
    requestAnimationFrame(() => {
      if (gridScrollRef.current) {
        gridScrollRef.current.scrollTop = savedScrollPositionRef.current;
      }
    });
  }, []);

  // Handle card selection in dialog - show preview instead of navigating
  const handleDialogCardSelect = useCallback((key: string) => {
    // Save scroll position before entering preview
    if (gridScrollRef.current) {
      savedScrollPositionRef.current = gridScrollRef.current.scrollTop;
    }
    setPreviewKey(key);
  }, []);

  // Handle approve/block from preview - return to grid
  const handlePreviewApprove = useCallback((key: string) => {
    onApprove(key);
    setPreviewKey(null);
  }, [onApprove]);

  const handlePreviewBlock = useCallback((key: string) => {
    onBlock(key);
    setPreviewKey(null);
  }, [onBlock]);

  // Reset preview when dialog closes
  const handleDialogChange = useCallback((open: boolean) => {
    if (!open) {
      setPreviewKey(null);
    }
    onDialogOpenChange(open);
  }, [onDialogOpenChange]);

  // Get fetched data for current preview (if any)
  const fetchedData = previewData ? fetchedEmailData.get(previewData.key) : null;

  // Sanitize body HTML for preview (use fetched body if original is missing)
  const sanitizedBodyHtml = useMemo(() => {
    const bodyHtml = previewData?.bodyHtml || fetchedData?.bodyHtml;
    if (!bodyHtml) return null;
    return sanitizeEmailHtml(bodyHtml);
  }, [previewData?.bodyHtml, fetchedData?.bodyHtml]);

  // Plain text body fallback (when HTML body is not available)
  const plainTextBody = fetchedData?.body || null;

  // Get unsubscribe URL (prefer original, fall back to fetched)
  const effectiveUnsubscribeUrl = previewData?.unsubscribeUrl || fetchedData?.unsubscribeUrl || null;

  return (
    <>
      <AnimatePresence>
        {(pendingCount > 0 || isLoading) && (
          <>
            {/* Mobile: Collapsed banner matching SurveyOverview style */}
            <motion.div
              {...slideInFromBottom}
              className={cn('sm:hidden rounded-lg border bg-card/50 overflow-hidden', className)}
            >
              <div className="flex w-full items-center gap-3 px-3 py-2.5 bg-primary/10 border-b border-primary/20">
                <button
                  type="button"
                  onClick={() => onDialogOpenChange(true)}
                  disabled={pendingCount === 0}
                  className="flex flex-1 items-center gap-0.5 sm:gap-3 text-left hover:opacity-80 transition-opacity"
                >
                  <AlertCircle className="h-4 w-4 text-primary hidden sm:block" />
                  <span className="font-medium text-xs sm:text-sm text-primary">
                    {pendingCount === 1 ? '1 new sender' : `${pendingCount} new senders`} to review
                  </span>
                  {isLoading && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary/70" />
                  )}
                  <div className="flex-1 hidden sm:block" />
                  <ChevronRight className="h-4 w-4 text-primary/70" />
                </button>
              </div>
            </motion.div>

            {/* Desktop: Full card carousel */}
            <motion.div
              {...slideInFromBottom}
              className={cn('hidden sm:block mb-3 rounded-2xl border border-border/40 bg-muted/50 overflow-hidden', className)}
            >
              <button
                type="button"
                onClick={() => onDialogOpenChange(true)}
                disabled={pendingCount === 0}
                className="group flex w-full items-center justify-between gap-3 px-4 py-3 transition-all hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold text-foreground">
                    {pendingCount === 1 ? '1 new sender' : `${pendingCount} new senders`}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {isLoading && (
                    <span className="flex items-center gap-1.5 rounded-full bg-muted/50 px-2.5 py-1 text-[10px] font-medium text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Syncing...
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                </div>
              </button>

              <div className="overflow-x-auto px-3 pb-3 scrollbar-hide">
                <div className={cn('flex min-w-0 gap-3')}>
                  {/* Only render first 15 cards in inline view for performance - full list is in dialog */}
                  {pendingSenders.slice(0, 15).map((sender) => (
                    <PendingSenderCard
                      key={sender.key}
                      data={sender}
                      onSelect={onSelect}
                      onApprove={onApprove}
                      onBlock={onBlock}
                      onUnsubscribe={onUnsubscribe}
                    />
                  ))}
                  {pendingSenders.length > 15 && (
                    <button
                      type="button"
                      onClick={() => onDialogOpenChange(true)}
                      className="flex-shrink-0 min-w-[120px] flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border/40 bg-card/30 p-4 text-muted-foreground hover:bg-card/50 hover:text-foreground transition-colors"
                    >
                      <span className="text-lg font-semibold">+{pendingSenders.length - 15}</span>
                      <span className="text-xs">more</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className={cn(
          // Full-screen on mobile: override the base max-h clamp and pad the
          // notch/home-bar insets directly instead.
          "h-full max-sm:max-h-full max-sm:pt-[var(--top-band)] max-sm:pb-[env(safe-area-inset-bottom,0px)] sm:h-[85vh] w-full p-0 overflow-hidden border-border/40 bg-background/80 backdrop-blur-xl shadow-2xl sm:rounded-xl flex flex-col",
          previewData ? "max-w-6xl" : "max-w-5xl"
        )}>
          <DialogHeader className="px-4 sm:px-6 py-4 mb-0! border-b border-border/40 bg-background/40 backdrop-blur-md sticky top-0 z-10 overflow-hidden">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-3 min-w-0 shrink-0">
                {previewData ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={handleClosePreview} className="h-8 gap-1 text-xs">
                      <ChevronLeft className="h-4 w-4" />
                      Back
                    </Button>
                    <DialogTitle className="text-lg font-semibold">Email Preview</DialogTitle>
                    <Badge variant="secondary" className="rounded-full px-2.5">
                      {previewIndex + 1} of {pendingCount}
                    </Badge>
                  </>
                ) : (
                  <>
                    <DialogTitle className="text-lg font-semibold">Pending Senders</DialogTitle>
                    <Badge variant="secondary" className="rounded-full px-2.5">{pendingCount}</Badge>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {previewData ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={!canGoPrev}
                      onClick={handlePrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={!canGoNext}
                      onClick={handleNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    {onOpenContacts && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={onOpenContacts} className="h-8 text-xs sm:gap-2">
                            <Users className="h-3.5 w-3.5 sm:hidden" />
                            <span className="hidden sm:inline">Manage contacts</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="sm:hidden">Manage contacts</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-8 gap-0 sm:gap-2 text-xs bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary border border-primary/10 shadow-none"
                          disabled={!pendingCount || bulkActionState === 'block'}
                          onClick={() => void onApproveAll()}
                        >
                          {bulkActionState === 'approve' ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="hidden sm:inline ml-2">
                                {bulkActionProgress
                                  ? `${bulkActionProgress.completed}/${bulkActionProgress.total}`
                                  : '...'}
                              </span>
                            </>
                          ) : (
                            <>
                              <Check className="h-3.5 w-3.5 text-primary" />
                              <span className="hidden sm:inline">Approve all</span>
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="sm:hidden">Approve all</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-8 gap-0 sm:gap-2 text-xs hover:bg-destructive/20 hover:text-destructive border border-destructive/10 shadow-none"
                          disabled={!pendingCount || bulkActionState === 'approve'}
                          onClick={() => void onBlockAll()}
                        >
                          {bulkActionState === 'block' ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span className="hidden sm:inline ml-2">
                                {bulkActionProgress
                                  ? `${bulkActionProgress.completed}/${bulkActionProgress.total}`
                                  : '...'}
                              </span>
                            </>
                          ) : (
                            <>
                              <ShieldOff className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Block all</span>
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="sm:hidden">Block all</TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>

          {previewData ? (
            // Preview mode - show email content
            <div className="flex flex-col flex-1 min-h-0">
              {/* Sender info header */}
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border/40 bg-muted/30 overflow-hidden">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-10 w-10 border border-border/40 shadow-sm shrink-0">
                    {(() => {
                      const logoUrl = getBrandfetchLogoUrl(previewData.senderEmail);
                      return logoUrl ? (
                        <AvatarImage
                          src={logoUrl}
                          alt={previewData.senderName ?? previewData.senderEmail}
                        />
                      ) : null;
                    })()}
                    <AvatarFallback className="bg-primary/5 text-primary/70 text-xs font-medium">
                      {getInitials(previewData.senderName ?? undefined, previewData.senderEmail)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {previewData.senderName ?? previewData.senderEmail}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {previewData.senderEmail}
                      {previewData.date && ` · ${formatPreviewDate(previewData.date)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 gap-2 text-sm bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary border border-primary/10 shadow-none whitespace-nowrap"
                    disabled={previewData.operationState === 'pending'}
                    onClick={() => handlePreviewApprove(previewData.key)}
                  >
                    {previewData.operationState === 'pending' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    Approve Sender
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-9 gap-2 text-sm hover:text-destructive hover:bg-destructive/10"
                    disabled={previewData.operationState === 'pending'}
                    onClick={() => handlePreviewBlock(previewData.key)}
                  >
                    <ShieldOff className="h-4 w-4" />
                    Block
                  </Button>
                  {effectiveUnsubscribeUrl && onUnsubscribe && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-9 w-9 p-0 text-muted-foreground hover:text-foreground"
                          disabled={previewData.operationState === 'pending'}
                          onClick={() => onUnsubscribe(previewData.key, effectiveUnsubscribeUrl)}
                        >
                          <MailMinus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        Unsubscribe & archive
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Email subject */}
              <div className="px-6 py-3 border-b border-border/40 overflow-hidden">
                <h2 className="text-lg font-semibold text-foreground truncate">
                  {previewData.subject || 'No subject'}
                </h2>
              </div>

              {/* Email body */}
              <ScrollArea className="flex-1 bg-white">
                <div className="p-6">
                  {isFetchingBody ? (
                    <div className="max-w-3xl mx-auto flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : sanitizedBodyHtml ? (
                    <div className="max-w-3xl mx-auto">
                      {/* Keyed by the previewed email (D5 class, N26): quoted-
                          collapse state and iframe height must not leak when
                          the preview switches sender. */}
                      <MessageBody
                        key={previewData.emailId ?? previewData.key}
                        sanitizedHtml={sanitizedBodyHtml}
                        minHeight="min-h-0"
                        contentClassName="!max-w-none"
                      />
                    </div>
                  ) : plainTextBody ? (
                    <div className="max-w-3xl mx-auto">
                      <p className="text-sm whitespace-pre-wrap">
                        {plainTextBody}
                      </p>
                    </div>
                  ) : (
                    <div className="max-w-3xl mx-auto">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {previewData.snippet || 'No content available'}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            // List mode - show sender cards
            <ScrollArea className="flex-1 min-h-0 bg-muted/5 overflow-hidden" viewportRef={gridScrollRef}>
              <div className="px-4 sm:px-6 py-0 overflow-hidden">
                {pendingCount === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card/50 p-12 text-center">
                    <div className="rounded-full bg-primary/10 p-3 mb-4">
                      <Check className="h-6 w-6 text-primary/60" />
                    </div>
                    <h3 className="text-sm font-medium text-foreground">All caught up</h3>
                    <p className="text-xs text-muted-foreground mt-1">No pending senders to review.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingSenders.map((sender) => (
                      <PendingSenderCard
                        key={`dialog-${sender.key}`}
                        data={sender}
                        layout="grid"
                        isSelected={sender.key === previewKey}
                        onSelect={handleDialogCardSelect}
                        onApprove={onApprove}
                        onBlock={onBlock}
                        onUnsubscribe={onUnsubscribe}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="px-6 py-3 border-t border-border/40 bg-background/40 backdrop-blur-md">
            <Button variant="ghost" size="sm" onClick={() => handleDialogChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
